import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { ApiError, api } from '../api';
import type { Role, Tag, User } from '../api';
import { useAuth, useTenantSession } from '../auth';
import {
  Banner,
  CredentialCallout,
  Empty,
  Field,
  Modal,
  Spinner,
  TagEditor,
  TagList,
} from '../components/ui';

export function Users() {
  const session = useTenantSession();
  const { token } = session;
  const { can } = useAuth();

  const [rows, setRows] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [credentials, setCredentials] = useState<{ email: string; password: string; reason?: string } | null>(null);
  const [editing, setEditing] = useState<User | 'new' | null>(null);
  const [assigning, setAssigning] = useState<User | null>(null);
  const [removing, setRemoving] = useState<User | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [userRes, roleRes] = await Promise.all([
        api.listUsers(token, search),
        can('role.read') ? api.listRoles(token) : Promise.resolve({ roles: [], total: 0 }),
      ]);
      setRows(userRes.users);
      setRoles(roleRes.roles);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load users');
    } finally {
      setLoading(false);
    }
  }, [token, search, can]);

  useEffect(() => {
    const t = setTimeout(() => void load(), search ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  async function onResetPassword(user: User) {
    setError(null);
    setNotice(null);
    setCredentials(null);
    try {
      const res = await api.resetUserPassword(token, user.id);
      if (res.temporaryPassword) {
        setCredentials({ email: user.email, password: res.temporaryPassword, reason: res.emailDelivery.error });
      } else {
        setNotice(`A new password was emailed to ${user.email}.`);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reset the password');
    }
  }

  async function onDelete() {
    if (!removing) return;
    try {
      await api.deleteUser(token, removing.id);
      setRemoving(null);
      void load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete the user');
      setRemoving(null);
    }
  }

  if (!can('user.read')) return <Empty>You do not have permission to view users.</Empty>;

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1>Users</h1>
          <p className="muted">Accounts in {session.application?.name ?? 'this application'}.</p>
        </div>
        {can('user.create') && (
          <button type="button" className="btn btn--primary" onClick={() => setEditing('new')}>
            New user
          </button>
        )}
      </div>

      {error && <Banner kind="error" onDismiss={() => setError(null)}>{error}</Banner>}
      {notice && <Banner kind="success" onDismiss={() => setNotice(null)}>{notice}</Banner>}
      {credentials && (
        <CredentialCallout
          email={credentials.email}
          password={credentials.password}
          reason={credentials.reason}
        />
      )}

      <input
        className="input input--search"
        placeholder="Search by name or email…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {loading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <Empty>No users match.</Empty>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Roles</th>
                <th>Tags</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((user) => (
                <tr key={user.id}>
                  <td>
                    {user.name}
                    {user.isMaster && <span className="pill pill--accent">Master</span>}
                    {user.id === session.user.id && <span className="pill pill--muted">You</span>}
                  </td>
                  <td>
                    {user.email}
                    {!user.emailVerified && <span className="pill pill--warn">Unverified</span>}
                  </td>
                  <td>
                    {user.roles && user.roles.length > 0 ? (
                      <span className="tag-list">
                        {user.roles.map((r) => (
                          <span className="chip" key={r}>
                            {r}
                          </span>
                        ))}
                      </span>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td>
                    <TagList tags={user.tags} />
                  </td>
                  <td className="right row-actions">
                    {can('role.assign') && (
                      <button type="button" className="btn btn--small" onClick={() => setAssigning(user)}>
                        Roles
                      </button>
                    )}
                    {can('user.update') && (
                      <>
                        <button type="button" className="btn btn--small" onClick={() => setEditing(user)}>
                          Edit
                        </button>
                        <button type="button" className="btn btn--small" onClick={() => void onResetPassword(user)}>
                          Reset password
                        </button>
                      </>
                    )}
                    {can('user.delete') && user.id !== session.user.id && (
                      <button type="button" className="btn btn--small btn--danger" onClick={() => setRemoving(user)}>
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <UserModal
          token={token}
          user={editing === 'new' ? null : editing}
          roles={roles}
          callerIsMaster={session.user.isMaster}
          onClose={() => setEditing(null)}
          onSaved={(result) => {
            setEditing(null);
            if (result) setCredentials(result);
            void load();
          }}
        />
      )}

      {assigning && (
        <AssignRolesModal
          token={token}
          user={assigning}
          roles={roles}
          onClose={() => setAssigning(null)}
          onSaved={() => {
            setAssigning(null);
            void load();
          }}
        />
      )}

      {removing && (
        <Modal
          title="Delete user"
          onClose={() => setRemoving(null)}
          onSubmit={(e) => {
            e.preventDefault();
            void onDelete();
          }}
          submitLabel="Delete"
          destructive
        >
          <Banner kind="warn">
            Delete <strong>{removing.email}</strong>? Their role assignments are removed with them.
          </Banner>
        </Modal>
      )}
    </div>
  );
}

function UserModal({
  token,
  user,
  roles,
  callerIsMaster,
  onClose,
  onSaved,
}: {
  token: string;
  user: User | null;
  roles: Role[];
  callerIsMaster: boolean;
  onClose: () => void;
  onSaved: (credentials: { email: string; password: string; reason?: string } | null) => void;
}) {
  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [isMaster, setIsMaster] = useState(user?.isMaster ?? false);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [tags, setTags] = useState<Tag[]>(user?.tags ?? []);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const toggle = (id: string) =>
    setSelectedRoles((prev) => (prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (user) {
        await api.updateUser(token, user.id, {
          name: name.trim(),
          email: email.trim(),
          isMaster,
          tags: tags.filter((t) => t.key.trim()),
        });
        onSaved(null);
      } else {
        // The password is always generated server-side and emailed; it only
        // comes back here when delivery failed.
        const res = await api.createUser(token, {
          name: name.trim(),
          email: email.trim(),
          isMaster,
          roleIds: selectedRoles,
          tags: tags.filter((t) => t.key.trim()),
        });
        onSaved(
          res.temporaryPassword
            ? { email: res.user.email, password: res.temporaryPassword, reason: res.emailDelivery.error }
            : null,
        );
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save the user');
      setSubmitting(false);
    }
  }

  return (
    <Modal title={user ? 'Edit user' : 'New user'} onClose={onClose} onSubmit={onSubmit} submitting={submitting}>
      {error && <Banner kind="error">{error}</Banner>}
      <Field label="Name">
        <input className="input" required value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field
        label="Email"
        hint={user ? 'Changing the email requires the new address to be verified again.' : undefined}
      >
        <input className="input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
      </Field>

      {!user && (
        <>
          <Banner kind="info">
            A password is generated and emailed to this address along with a verification link.
          </Banner>
          {roles.length > 0 && (
            <Field label={`Roles (${selectedRoles.length} selected)`}>
              <div className="checklist">
                {roles.map((r) => (
                  <label className="checklist__item" key={r.id}>
                    <input type="checkbox" checked={selectedRoles.includes(r.id)} onChange={() => toggle(r.id)} />
                    <span>{r.name}</span>
                  </label>
                ))}
              </div>
            </Field>
          )}
        </>
      )}

      {callerIsMaster && (
        <label className="checkbox-row">
          <input type="checkbox" checked={isMaster} onChange={(e) => setIsMaster(e.target.checked)} />
          <span>
            Master user
            <span className="muted small"> — holds every permission in this application</span>
          </span>
        </label>
      )}

      <Field label="Tags">
        <TagEditor value={tags} onChange={setTags} />
      </Field>
    </Modal>
  );
}

function AssignRolesModal({
  token,
  user,
  roles,
  onClose,
  onSaved,
}: {
  token: string;
  user: User;
  roles: Role[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // The list view only carries role names, so fetch the authoritative ids.
  useEffect(() => {
    let cancelled = false;
    api
      .getUser(token, user.id)
      .then((res) => {
        if (!cancelled) setSelected(res.roles.map((r) => r.id));
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Could not load current roles');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, user.id]);

  const toggle = (id: string) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.setUserRoles(token, user.id, selected);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not update role assignments');
      setSubmitting(false);
    }
  }

  return (
    <Modal title={`Roles for ${user.name}`} onClose={onClose} onSubmit={onSubmit} submitting={submitting || loading}>
      {error && <Banner kind="error">{error}</Banner>}
      {user.isMaster && (
        <Banner kind="info">
          This is a master user — they hold every permission regardless of the roles selected here.
        </Banner>
      )}
      {loading ? (
        <Spinner label="Loading roles…" />
      ) : roles.length === 0 ? (
        <p className="muted">No roles have been defined yet.</p>
      ) : (
        <div className="checklist">
          {roles.map((r) => (
            <label className="checklist__item" key={r.id}>
              <input type="checkbox" checked={selected.includes(r.id)} onChange={() => toggle(r.id)} />
              <span>
                {r.name}
                <span className="muted small"> — {r.permissions.length} permission(s)</span>
              </span>
            </label>
          ))}
        </div>
      )}
    </Modal>
  );
}
